import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AdminHome } from './components/Admin/AdminHome';
import { CreatePanel } from './components/Admin/CreatePanel';
import { ManagePanels } from './components/Admin/ManagePanels';
import { UploadPDF } from './components/Admin/UploadPDF';
import { ManageSuppliers } from './components/Admin/ManageSuppliers';
import { ManageSupplierRequests } from './components/Admin/ManageSupplierRequests';
import { SearchAnalytics } from './components/Admin/SearchAnalytics';
import { ManageInventory } from './components/Admin/ManageInventory';
import { SupplierHome } from './components/Supplier/SupplierHome';
import { SupplierInventory } from './components/Supplier/SupplierInventory';
import { SupplierLogin } from './components/Supplier/SupplierLogin';
import { MyPanelRequests } from './components/Supplier/MyPanelRequests';
import { isAuthenticated, getAuthData } from './utils/auth';
import { Login } from './components/Auth/login';
import { SetPassword } from './components/Auth/SetPassword';
import { ForgotPassword } from './components/Auth/ForgotPassword';
import { Layout } from './components/Layout/layout';
import { SearchPanels } from './components/User/SearchPanel';
import { RequestPanelPage } from './components/User/RequestPanelPage';
import { UserSignup } from './components/User/UserSignup';
import { UserLogin } from './components/User/UserLogin';
import { EmailVerification } from './components/User/EmailVerification';
import { PanelRequests } from './components/Admin/PanelRequestsNew';
import { RequestPanelForm } from './components/Supplier/RequestPanelFormNew';
import { SolarMonitoring } from './components/Admin/SolarMonitoring';

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: string }> = ({ children, requiredRole }) => {
  const authData = getAuthData();
  const isAuth = isAuthenticated();

  console.log("Auth Data", authData)
  console.log("is Auth", isAuth)
  
  if (!isAuth) {
    return <Navigate to="/login" />;
  }
  
  if (requiredRole && authData?.user.userType !== requiredRole) {
    return <Navigate to={authData?.user.userType === 'supplier' ? '/supplier' : '/admin'} />;
  }
  
  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return !isAuthenticated() ? <>{children}</> : <Navigate to="/admin" />;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        
        <Route
          path="/supplier/login"
          element={
            <PublicRoute>
              <SupplierLogin />
            </PublicRoute>
          }
        />
        
        <Route
          path="/set-password"
          element={<SetPassword />}
        />

        <Route
          path="/user/signup"
          element={<UserSignup />}
        />

        <Route
          path="/user/login"
          element={<UserLogin />}
        />

        <Route
          path="/verify-email"
          element={<EmailVerification />}
        />

        <Route
          path="/forgot-password"
          element={<ForgotPassword />}
        />
        
        <Route
          path="/"
          element={
            <Layout>
              <SearchPanels />
            </Layout>
          }
        />
        
        <Route
          path="/search"
          element={
            <Layout>
              <SearchPanels />
            </Layout>
          }
        />

        <Route
          path="/request-panel"
          element={<RequestPanelPage />}
        />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <AdminHome />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/create"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <CreatePanel />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/panels"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ManagePanels />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/upload"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <UploadPDF />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/suppliers"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ManageSuppliers />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <SearchAnalytics />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/requests"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <PanelRequests />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/supplier-requests"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ManageSupplierRequests />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/search-logs"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <SearchAnalytics />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/inventory"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <ManageInventory />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/solar-monitoring"
          element={
            <ProtectedRoute requiredRole="admin">
              <Layout>
                <SolarMonitoring />
              </Layout>
            </ProtectedRoute>
          }
        />

        {/* Supplier Routes */}
        <Route
          path="/supplier"
          element={
            <ProtectedRoute requiredRole="supplier">
              <Layout>
                <SupplierHome />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/supplier/inventory"
          element={
            <ProtectedRoute requiredRole="supplier">
              <Layout>
                <SupplierInventory />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/supplier/requests"
          element={
            <ProtectedRoute requiredRole="supplier">
              <Layout>
                <MyPanelRequests />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/supplier/request-panel"
          element={
            <ProtectedRoute requiredRole="supplier">
              <Layout>
                <RequestPanelForm />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;